import { Capabilities } from "@msys/common";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Typography,
} from "@mui/material";
import { Link } from "react-router-dom";
import { PermissionName } from "../../../clients/graphqlTypes.js";
import {
  RestrictedByAnyCapabilityWithDebug,
  RestrictedByCapabilityWithDebug,
} from "../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../auth/RestrictedByOrganisationPermission.js";
import { SidebarGroup, SidebarItem } from "../../global/SidebarProvider.js";

export interface PageLeftSidebarGroup<T extends SidebarItem = SidebarItem> {
  name: string;
  items: T[];
  capability?: Capabilities;
  anyCapabilities?: Capabilities[];
  permission?: PermissionName;
}

export const PageLeftSidebar = ({
  groups,
  activeItem,
  onItemClick,
  variant = "regular",
}: {
  groups: SidebarGroup[];
  activeItem: SidebarItem | undefined;
  onItemClick?: () => void;
  variant?: "dense" | "regular";
}) => {
  if (groups.length === 0) return null;
  return (
    <>
      {groups.map((group, groupIndex) => {
        if (group.items.length === 0) return null;
        let listGroup = (
          <List
            key={`${group.name}-${groupIndex}`}
            component="nav"
            dense={variant === "dense"}
            subheader={
              <ListSubheader
                component="div"
                sx={{
                  whiteSpace: "nowrap",
                  backgroundColor: "transparent",
                }}
              >
                {group.name}
              </ListSubheader>
            }
          >
            {group.items.map((item, itemIndex) => {
              let listItem = (
                <ListItemButton
                  key={`${item.name}-${itemIndex}`}
                  // @ts-ignore
                  component={Link}
                  to={item.path}
                  selected={activeItem === item}
                  onClick={onItemClick}
                >
                  <ListItemIcon
                    sx={theme => ({
                      minWidth: theme.layout.listItemMinWidth.sm,
                    })}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText>
                    <Typography noWrap>{item.label}</Typography>
                  </ListItemText>
                </ListItemButton>
              );

              if (item.permission) {
                listItem = (
                  <RestrictedByOrganisationPermissionWithDebug
                    permission={item.permission}
                    key={`${item.name}-${itemIndex}-permission`}
                  >
                    {listItem}
                  </RestrictedByOrganisationPermissionWithDebug>
                );
              }
              if (item.anyCapabilities) {
                listItem = (
                  <RestrictedByAnyCapabilityWithDebug
                    capabilities={item.anyCapabilities}
                    key={`${item.name}-${itemIndex}-any-capability`}
                  >
                    {listItem}
                  </RestrictedByAnyCapabilityWithDebug>
                );
              }
              if (item.capability) {
                listItem = (
                  <RestrictedByCapabilityWithDebug
                    capability={item.capability}
                    key={`${item.name}-${itemIndex}-capability`}
                  >
                    {listItem}
                  </RestrictedByCapabilityWithDebug>
                );
              }

              return listItem;
            })}
          </List>
        );

        if (group.permission) {
          listGroup = (
            <RestrictedByOrganisationPermissionWithDebug
              permission={group.permission}
              key={`${group.name}-${groupIndex}-permission`}
            >
              {listGroup}
            </RestrictedByOrganisationPermissionWithDebug>
          );
        }
        if (group.anyCapabilities) {
          listGroup = (
            <RestrictedByAnyCapabilityWithDebug
              capabilities={group.anyCapabilities}
              key={`${group.name}-${groupIndex}-any-capability`}
            >
              {listGroup}
            </RestrictedByAnyCapabilityWithDebug>
          );
        }
        if (group.capability) {
          listGroup = (
            <RestrictedByCapabilityWithDebug
              capability={group.capability}
              key={`${group.name}-${groupIndex}-capability`}
            >
              {listGroup}
            </RestrictedByCapabilityWithDebug>
          );
        }

        return listGroup;
      })}
    </>
  );
};
